import { useState, useMemo, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Calculator, User, UserRound, Flame, Dumbbell, Bike, Zap, Sofa,
  TrendingDown, TrendingUp, Minus, Rocket, Turtle, Wind,
  Leaf, Beef, Wheat, Cookie, Save, Target, Activity, HeartPulse,
  UtensilsCrossed, Plus, X as XIcon, Scale, ArrowDown, ArrowUp,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Reference data
// ---------------------------------------------------------------------------
const ACTIVITY = [
  { id: "sedentary",   mult: 1.2,   icon: Sofa,     title: "خامل",
    desc: "شغل مكتبي، جلوس معظم اليوم، بدون تمارين",
    steps: "أقل من 5,000 خطوة" },
  { id: "light",       mult: 1.375, icon: Wind,     title: "خفيف",
    desc: "وقوف ومشي بسيط خلال اليوم",
    steps: "5,000 – 7,500 خطوة" },
  { id: "moderate",    mult: 1.55,  icon: Bike,     title: "معتدل",
    desc: "مشي منتظم أو وظيفة تتطلب حركة",
    steps: "7,500 – 10,000 خطوة" },
  { id: "active",      mult: 1.725, icon: Dumbbell, title: "نشط",
    desc: "مهنة نشطة (تدريس، تمريض، مبيعات ميدانية)",
    steps: "10,000 – 12,500 خطوة" },
  { id: "very_active", mult: 1.9,   icon: Zap,      title: "نشط جداً",
    desc: "عمل بدني شاق (بناء، مزارع، ساعي بريد)",
    steps: "أكثر من 12,500 خطوة" },
];
// NOTE: هذا التصنيف يمثّل الحركة اليومية فقط (المشي والعمل).
// التمارين تحسب لاحقاً ولا تدخل هنا.

const SPEEDS_LOSS = [
  { id: "slow",         kg: 0.25, icon: Turtle, title: "بطيء وثابت",  desc: "0.25 كجم / أسبوع" },
  { id: "moderate",     kg: 0.5,  icon: Wind,   title: "متوازن",       desc: "0.5 كجم / أسبوع" },
  { id: "fast",         kg: 0.75, icon: Bike,   title: "سريع",         desc: "0.75 كجم / أسبوع" },
  { id: "aggressive",   kg: 1.0,  icon: Rocket, title: "قوي جداً",     desc: "1.0 كجم / أسبوع" },
];
const SPEEDS_GAIN = [
  { id: "slow",       kg: 0.15, icon: Turtle, title: "لين نظيف",     desc: "0.15 كجم / أسبوع" },
  { id: "moderate",   kg: 0.3,  icon: Wind,   title: "متوسط",         desc: "0.3 كجم / أسبوع" },
  { id: "fast",       kg: 0.5,  icon: Bike,   title: "سريع",          desc: "0.5 كجم / أسبوع" },
  { id: "aggressive", kg: 0.75, icon: Rocket, title: "قوي",           desc: "0.75 كجم / أسبوع" },
];

const DIETS = [
  { id: "classic",       p: 25, c: 45, f: 30, icon: Leaf,   title: "كلاسيك (متوازن)",  desc: "بروتين، كارب، دهون بنسب متوازنة" },
  { id: "high_protein",  p: 40, c: 35, f: 25, icon: Beef,   title: "عالي البروتين",     desc: "مناسب للتضخيم وحماية العضلة" },
  { id: "low_carb",      p: 30, c: 20, f: 50, icon: Wheat,  title: "قليل الكارب",       desc: "شهية أقل + طاقة ثابتة" },
  { id: "keto",          p: 25, c:  5, f: 70, icon: Cookie, title: "كيتو",              desc: "دهون عالية، كارب أقل من 30 جم" },
];

// ---------------------------------------------------------------------------
// Math
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Pure calculator — used both by the manual compute button and auto-recompute
// after a new weigh-in.
// ---------------------------------------------------------------------------
function calculateProfile({ gender, age, height, currentWeight, targetWeight, activity, speed, diet }) {
  const goal = derivedGoal(currentWeight, targetWeight);
  const isMaintain = goal === "maintain";
  const bmr = computeBMR({ gender, weight: currentWeight, height, age });
  const act = ACTIVITY.find((a) => a.id === activity);
  const tdee = Math.round(bmr * (act?.mult ?? 1.55));
  const speedOptions = goal === "gain" ? SPEEDS_GAIN : SPEEDS_LOSS;
  const speedKg = isMaintain ? 0 : (speedOptions.find((s) => s.id === speed)?.kg ?? 0);
  const dailyDelta = Math.round((speedKg * 7700) / 7);
  const target = isMaintain
    ? tdee
    : goal === "lose" ? tdee - dailyDelta : tdee + dailyDelta;
  const d = DIETS.find((x) => x.id === diet);
  const protein = Math.round((target * d.p) / 100 / 4);
  const carbs = Math.round((target * d.c) / 100 / 4);
  const fat = Math.round((target * d.f) / 100 / 9);
  return {
    bmr: Math.round(bmr), tdee, target, goal, diet: d, speedKg,
    macros: { protein, carbs, fat },
  };
}

function computeBMR({ gender, weight, height, age }) {
  const w = Number(weight), h = Number(height), a = Number(age);
  if (!w || !h || !a) return 0;
  return gender === "female"
    ? 10 * w + 6.25 * h - 5 * a - 161
    : 10 * w + 6.25 * h - 5 * a + 5;
}

function derivedGoal(currentWeight, targetWeight) {
  const c = Number(currentWeight), t = Number(targetWeight);
  if (!c || !t) return null;
  if (Math.abs(c - t) < 0.5) return "maintain";
  return t < c ? "lose" : "gain";
}

function bmiClass(bmi) {
  if (!bmi) return null;
  if (bmi < 18.5)   return { label: "نقص وزن",       color: "text-sky-700 bg-sky-50 border-sky-200",   note: "احرص على زيادة صحية للوزن مع بروتين كافٍ." };
  if (bmi < 25)     return { label: "وزن صحي",      color: "text-emerald-700 bg-emerald-50 border-emerald-200", note: "ممتاز — استمر بنفس النمط للمحافظة." };
  if (bmi < 30)     return { label: "زيادة وزن",     color: "text-amber-700 bg-amber-50 border-amber-200", note: "نزول 5–10٪ من وزنك يحسن صحتك كثيراً." };
  if (bmi < 35)     return { label: "سمنة درجة أولى", color: "text-orange-700 bg-orange-50 border-orange-200", note: "خطة تدريجية أفضل من الحمية العنيفة." };
  if (bmi < 40)     return { label: "سمنة درجة ثانية", color: "text-red-700 bg-red-50 border-red-200", note: "ينصح بمتابعة طبية بجانب الخطة." };
  return                    { label: "سمنة مفرطة",     color: "text-red-800 bg-red-100 border-red-300", note: "استشارة طبية ضرورية قبل خفض السعرات." };
}

// Format any number in Latin/English digits — user preference.
const fmtNum = (n) => (n ?? 0).toLocaleString("en-US");

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------
function Tile({ active, onClick, icon: Icon, title, desc, extra, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-start p-4 rounded-2xl border-2 transition-all hover:-translate-y-0.5 ${
        active
          ? "border-orange-500 bg-orange-500/10 shadow-md shadow-orange-500/10"
          : "border-foreground/10 bg-card hover:border-orange-300"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 grid place-items-center rounded-xl shrink-0 ${
          active ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-600"
        }`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm">{title}</p>
          {desc && <p className="text-xs text-foreground/60 mt-0.5 leading-relaxed">{desc}</p>}
          {extra && <p className="text-[11px] text-orange-600 font-medium mt-1">{extra}</p>}
        </div>
      </div>
    </button>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <Card className="p-5 md:p-6 space-y-4 border-foreground/10">
      <div>
        <h3 className="font-display font-semibold text-lg">{title}</h3>
        {subtitle && <p className="text-xs text-foreground/60 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function CaloriesCalculatorPage() {
  const [gender, setGender] = useState("male");
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [currentWeight, setCurrentWeight] = useState("");
  const [targetWeight, setTargetWeight] = useState("");
  const [activity, setActivity] = useState("moderate");
  const [speed, setSpeed] = useState("moderate");
  const [diet, setDiet] = useState("classic");
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(null);      // {start_date, ...}
  const [saving, setSaving] = useState(false);
  const [latestWeight, setLatestWeight] = useState(null); // most recent weigh-in

  // Load saved profile + weight logs on mount — prefill form + show live progress.
  useEffect(() => {
    (async () => {
      try {
        const [profRes, wtRes] = await Promise.all([
          api.get("/users/me/calorie-profile"),
          api.get("/users/me/weight-logs").catch(() => ({ data: [] })),
        ]);
        const data = profRes.data;
        const wts = Array.isArray(wtRes.data) ? wtRes.data : [];
        if (wts.length > 0) setLatestWeight(wts[0]);   // API sorts date desc
        if (!data) return;
        setGender(data.gender || "male");
        setAge(String(data.age || ""));
        setHeight(String(data.height || ""));
        setCurrentWeight(String(data.current_weight || ""));
        setTargetWeight(String(data.target_weight || ""));
        setActivity(data.activity || "moderate");
        setSpeed(data.speed || "moderate");
        setDiet(data.diet || "classic");
        setSaved(data);
        if (data.target_kcal) {
          setResult({
            bmr: data.bmr, tdee: data.tdee, target: data.target_kcal,
            goal: derivedGoal(data.current_weight, data.target_weight),
            diet: DIETS.find((x) => x.id === data.diet) || DIETS[0],
            speedKg: data.speed_kg_per_week || 0,
            macros: data.macros || { protein: 0, carbs: 0, fat: 0 },
            savedStartDate: data.start_date,
          });
        }
      } catch { /* not signed in / no profile yet — silent */ }
    })();
  }, []);

  const goal = derivedGoal(currentWeight, targetWeight);
  const isMaintain = goal === "maintain";
  const canSubmit = age && height && currentWeight && targetWeight && goal;
  const speedOptions = goal === "gain" ? SPEEDS_GAIN : SPEEDS_LOSS;

  const compute = () => {
    const r = calculateProfile({
      gender, age, height, currentWeight, targetWeight, activity, speed, diet,
    });
    setResult({ ...r, savedStartDate: null });
    setTimeout(() => {
      document.getElementById("calc-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  // Auto-recompute when the user logs a new weight — new BMR → new TDEE →
  // new target kcal and macros. Then persist to the profile so remaining
  // days + BMI + all charts stay in sync.
  const handleWeightLogged = async (newKg) => {
    if (!newKg || !saved) return;
    setCurrentWeight(String(newKg));
    const r = calculateProfile({
      gender, age, height,
      currentWeight: newKg,
      targetWeight, activity, speed, diet,
    });
    setResult((prev) => ({ ...r, savedStartDate: prev?.savedStartDate || saved.start_date }));
    try {
      const { data } = await api.put("/users/me/calorie-profile", {
        gender,
        age: Number(age),
        height: Number(height),
        current_weight: Number(newKg),
        target_weight: Number(targetWeight),
        activity, speed, diet,
        target_kcal: r.target,
        macros: r.macros,
        speed_kg_per_week: r.speedKg,
        bmr: r.bmr,
        tdee: r.tdee,
      });
      setSaved(data);
      toast.success(`تم تحديث سعراتك — الهدف الجديد ${r.target.toLocaleString("en-US")} سعرة/يوم`);
    } catch { /* silent — UI still updated locally */ }
  };

  const saveProfile = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const { data } = await api.put("/users/me/calorie-profile", {
        gender,
        age: Number(age),
        height: Number(height),
        current_weight: Number(currentWeight),
        target_weight: Number(targetWeight),
        activity, speed, diet,
        target_kcal: result.target,
        macros: result.macros,
        speed_kg_per_week: result.speedKg,
        bmr: result.bmr,
        tdee: result.tdee,
      });
      setSaved(data);
      setResult((r) => ({ ...r, savedStartDate: data.start_date }));
      toast.success("تم حفظ خطتك — راح تشوفها كل مرة تفتح الصفحة");
    } catch {
      toast.error("تعذّر الحفظ. حاول مرة ثانية.");
    } finally {
      setSaving(false);
    }
  };

  const goalChip = useMemo(() => {
    if (!goal) return null;
    if (goal === "lose")    return { icon: TrendingDown, label: "خفض وزن",           color: "text-blue-600 bg-blue-50" };
    if (goal === "gain")    return { icon: TrendingUp,   label: "زيادة وزن",         color: "text-emerald-600 bg-emerald-50" };
    return                         { icon: Minus,        label: "المحافظة على الوزن", color: "text-foreground/70 bg-foreground/5" };
  }, [goal]);

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-5" data-testid="calories-calculator-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-orange-500 text-white grid place-items-center shadow-lg shadow-orange-500/30">
          <Calculator className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-2xl md:text-3xl font-bold">احسب سعراتك</h1>
          <p className="text-sm text-foreground/60">أدخل بياناتك وسنحسب لك السعرات والماكروز اللازمة لهدفك</p>
        </div>
      </div>

      {/* 1. Gender */}
      <SectionCard title="النوع">
        <div className="grid grid-cols-2 gap-3">
          <Tile active={gender === "male"}   onClick={() => setGender("male")}   icon={User}       title="ذكر"  testid="calc-gender-male" />
          <Tile active={gender === "female"} onClick={() => setGender("female")} icon={UserRound}  title="أنثى" testid="calc-gender-female" />
        </div>
      </SectionCard>

      {/* 2. Body metrics */}
      <SectionCard title="مقاييس الجسم" subtitle="سنكتشف هدفك تلقائياً من وزنك الحالي والمستهدف">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-foreground/70">العمر (سنة)</Label>
            <Input type="number" inputMode="numeric" min="10" max="100" placeholder="مثلاً 28"
                   value={age} onChange={(e) => setAge(e.target.value)} data-testid="calc-age" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-foreground/70">الطول (سم)</Label>
            <Input type="number" inputMode="numeric" min="120" max="230" placeholder="مثلاً 175"
                   value={height} onChange={(e) => setHeight(e.target.value)} data-testid="calc-height" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-foreground/70">الوزن الحالي (كجم)</Label>
            <Input type="number" inputMode="decimal" step="0.1" min="30" max="250" placeholder="مثلاً 80"
                   value={currentWeight} onChange={(e) => setCurrentWeight(e.target.value)} data-testid="calc-current-weight" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-foreground/70">الوزن المستهدف (كجم)</Label>
            <Input type="number" inputMode="decimal" step="0.1" min="30" max="250" placeholder="مثلاً 72"
                   value={targetWeight} onChange={(e) => setTargetWeight(e.target.value)} data-testid="calc-target-weight" />
          </div>
        </div>
        {goalChip && (
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${goalChip.color}`} data-testid="calc-goal-chip">
            <goalChip.icon className="h-3.5 w-3.5" />
            <span>هدفك: {goalChip.label}</span>
          </div>
        )}
      </SectionCard>

      {/* 3. Activity level */}
      <SectionCard title="مستوى النشاط اليومي"
                   subtitle="الحركة اليومية من غير التمارين (المشي والعمل). التمارين تحتسب بشكل منفصل.">
        <div className="grid sm:grid-cols-2 gap-3">
          {ACTIVITY.map((a) => (
            <Tile key={a.id} active={activity === a.id} onClick={() => setActivity(a.id)}
                  icon={a.icon} title={a.title} desc={a.desc} extra={a.steps}
                  testid={`calc-activity-${a.id}`} />
          ))}
        </div>
      </SectionCard>

      {/* 4. Speed of progress — hidden on maintain */}
      {goal && !isMaintain && (
        <SectionCard title="سرعة التقدم" subtitle={goal === "lose" ? "كم كيلو تحب تفقد بالأسبوع" : "كم كيلو تحب تكسب بالأسبوع"}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {speedOptions.map((s) => (
              <Tile key={s.id} active={speed === s.id} onClick={() => setSpeed(s.id)}
                    icon={s.icon} title={s.title} desc={s.desc} testid={`calc-speed-${s.id}`} />
            ))}
          </div>
        </SectionCard>
      )}

      {/* 5. Diet preference */}
      <SectionCard title="نمط الأكل المفضل">
        <div className="grid sm:grid-cols-2 gap-3">
          {DIETS.map((d) => (
            <Tile key={d.id} active={diet === d.id} onClick={() => setDiet(d.id)}
                  icon={d.icon} title={d.title} desc={d.desc} testid={`calc-diet-${d.id}`} />
          ))}
        </div>
      </SectionCard>

      {/* Submit */}
      <div className="sticky bottom-4 z-10">
        <Button
          onClick={compute}
          disabled={!canSubmit}
          className="w-full h-14 text-base font-bold bg-orange-500 hover:bg-orange-600 text-white shadow-xl shadow-orange-500/30 disabled:opacity-50"
          data-testid="calc-submit"
        >
          <Flame className="h-5 w-5 ml-2" />
          احسب سعراتي
        </Button>
        {!canSubmit && (
          <p className="text-center text-xs text-foreground/50 mt-2">أدخل عمرك وطولك ووزنك الحالي والمستهدف أولاً</p>
        )}
      </div>

      {/* Result */}
      {result && (
        <ResultCard
          result={result}
          heightCm={Number(height)}
          currentWeight={Number(currentWeight)}
          latestLoggedWeight={latestWeight?.weight_kg}
          latestLoggedDate={latestWeight?.date}
          targetWeight={Number(targetWeight)}
          onSave={saveProfile}
          saving={saving}
          isSaved={!!saved}
        />
      )}

      {/* Daily meal log + merged weigh-in / goal progress — only after profile is saved */}
      {saved && (
        <>
          <MealLogCard targetKcal={saved.target_kcal} targetMacros={saved.macros} />
          <WeightLogCard
            targetWeight={Number(targetWeight)}
            heightCm={Number(height)}
            initialWeight={Number(currentWeight)}
            speedKgPerWeek={saved.speed_kg_per_week}
            startDate={saved.start_date}
            onLatestChange={setLatestWeight}
            onWeightLogged={handleWeightLogged}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------
function ResultCard({ result, heightCm, currentWeight, latestLoggedWeight, latestLoggedDate, targetWeight, onSave, saving, isSaved }) {
  const { bmr, tdee, target, goal, diet, speedKg, macros } = result;
  const goalLabel = goal === "lose" ? "لتخفيف الوزن" : goal === "gain" ? "لزيادة الوزن" : "للمحافظة على الوزن";
  const proteinKcal = macros.protein * 4;
  const carbsKcal = macros.carbs * 4;
  const fatKcal = macros.fat * 9;

  return (
    <div id="calc-result" className="scroll-mt-4 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="calc-result">
      {/* Hero */}
      <Card className="p-6 md:p-8 bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0 shadow-2xl shadow-orange-500/30">
        <p className="text-sm opacity-90">سعراتك اليومية {goalLabel}</p>
        <p className="font-display font-black text-5xl md:text-6xl mt-2 tabular-nums" data-testid="calc-result-target" dir="ltr">
          {fmtNum(target)}
          <span className="text-xl font-normal opacity-80 mr-2 ms-2">سعرة / يوم</span>
        </p>
        <div className="flex flex-wrap gap-2 mt-4 text-xs">
          <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur">معدل الأيض: {fmtNum(bmr)}</span>
          <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur">احتياجك اليومي: {fmtNum(tdee)}</span>
          {speedKg > 0 && (
            <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur" dir="ltr">{speedKg} كجم / أسبوع</span>
          )}
          <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur">{diet.title}</span>
        </div>
      </Card>

      {/* Macros */}
      <Card className="p-5 md:p-6 border-foreground/10">
        <h3 className="font-display font-semibold text-lg mb-4">توزيع الماكروز</h3>
        <div className="grid grid-cols-3 gap-3">
          <MacroTile label="بروتين" grams={macros.protein} kcal={proteinKcal} pct={diet.p} color="bg-rose-500"    testid="macro-protein" />
          <MacroTile label="كارب"   grams={macros.carbs}   kcal={carbsKcal}   pct={diet.c} color="bg-amber-500"   testid="macro-carbs" />
          <MacroTile label="دهون"   grams={macros.fat}     kcal={fatKcal}     pct={diet.f} color="bg-sky-500"     testid="macro-fat" />
        </div>
        <div className="mt-5 h-3 rounded-full overflow-hidden flex">
          <div className="bg-rose-500"  style={{ width: `${diet.p}%` }} />
          <div className="bg-amber-500" style={{ width: `${diet.c}%` }} />
          <div className="bg-sky-500"   style={{ width: `${diet.f}%` }} />
        </div>
        <p className="text-xs text-foreground/60 mt-3 leading-relaxed">
          <span className="inline-block h-2 w-2 rounded-full bg-rose-500 ml-1"></span> بروتين {diet.p}٪ ·
          <span className="inline-block h-2 w-2 rounded-full bg-amber-500 mx-1"></span> كارب {diet.c}٪ ·
          <span className="inline-block h-2 w-2 rounded-full bg-sky-500 mx-1"></span> دهون {diet.f}٪
        </p>
      </Card>

      {/* Save button — plain, no note wrapper */}
      <Button
        onClick={onSave}
        disabled={saving}
        className="w-full sm:w-auto bg-foreground text-background hover:bg-foreground/90 gap-2"
        data-testid="calc-save"
      >
        <Save className="h-4 w-4" />
        {saving ? "جاري الحفظ..." : isSaved ? "تحديث خطتي" : "احفظ خطتي"}
      </Button>
      {isSaved && (
        <p className="text-xs text-emerald-700 flex items-center gap-1">
          <Activity className="h-3 w-3" /> خطتك محفوظة — راح تشوفها كل مرة تفتح الصفحة
        </p>
      )}
    </div>
  );
}

function MacroTile({ label, grams, kcal, pct, color, testid }) {
  return (
    <div className="rounded-2xl border border-foreground/10 p-4 text-center" data-testid={testid}>
      <div className={`h-1.5 w-8 rounded-full ${color} mx-auto mb-3`} />
      <p className="text-xs text-foreground/60">{label}</p>
      <p className="font-display font-bold text-2xl mt-1 tabular-nums" dir="ltr">
        {fmtNum(grams)}<span className="text-sm font-normal text-foreground/50 mr-1">جم</span>
      </p>
      <p className="text-[10px] text-foreground/50 mt-1" dir="ltr">{fmtNum(kcal)} سعرة · {pct}٪</p>
    </div>
  );
}

// ===========================================================================
// Daily meal log — user adds meals, we sum kcal + macros for today and show
// remaining against the saved target.
// ===========================================================================
function MealLogCard({ targetKcal, targetMacros }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [kcal, setKcal] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [busy, setBusy] = useState(false);
  const [dateISO, setDateISO] = useState("");

  const reload = async () => {
    try {
      const { data } = await api.get("/users/me/meal-logs");
      setItems(data.items || []);
      setDateISO(data.date);
    } catch { /* silent */ }
  };
  useEffect(() => { reload(); }, []);

  const eaten = items.reduce(
    (acc, it) => ({
      kcal: acc.kcal + (it.kcal || 0),
      protein: acc.protein + (it.protein_g || 0),
      carbs: acc.carbs + (it.carbs_g || 0),
      fat: acc.fat + (it.fat_g || 0),
    }),
    { kcal: 0, protein: 0, carbs: 0, fat: 0 }
  );
  const remaining = Math.max(0, (targetKcal || 0) - eaten.kcal);
  const overshoot = eaten.kcal - (targetKcal || 0);
  const pct = targetKcal ? Math.min(100, Math.round((eaten.kcal / targetKcal) * 100)) : 0;

  const addMeal = async () => {
    if (!name.trim() || !kcal) return;
    setBusy(true);
    try {
      await api.post("/users/me/meal-logs", {
        name: name.trim(),
        kcal: Number(kcal),
        protein_g: Number(protein) || 0,
        carbs_g: Number(carbs) || 0,
        fat_g: Number(fat) || 0,
      });
      setName(""); setKcal(""); setProtein(""); setCarbs(""); setFat("");
      await reload();
    } catch { toast.error("تعذّر إضافة الوجبة"); }
    finally { setBusy(false); }
  };

  const removeMeal = async (id) => {
    try { await api.delete(`/users/me/meal-logs/${id}`); reload(); }
    catch { toast.error("تعذّر الحذف"); }
  };

  return (
    <Card className="p-5 md:p-6 border-foreground/10" data-testid="meal-log-card">
      <div className="flex items-center gap-2 mb-4">
        <UtensilsCrossed className="h-5 w-5 text-orange-500" />
        <h3 className="font-display font-semibold text-lg">وجباتي اليوم</h3>
      </div>

      {/* Consumed vs remaining */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatBox label="أكلت" value={fmtNum(eaten.kcal)} unit="سعرة" testid="meal-eaten" />
        <StatBox label={overshoot > 0 ? "تجاوز" : "الباقي"}
                 value={overshoot > 0 ? `+${fmtNum(overshoot)}` : fmtNum(remaining)}
                 unit="سعرة"
                 accent={overshoot > 0 ? "text-red-600" : "text-emerald-600"}
                 testid="meal-remaining" />
        <StatBox label="الهدف" value={fmtNum(targetKcal || 0)} unit="سعرة" testid="meal-target" />
      </div>
      <div className="h-2 rounded-full bg-foreground/10 overflow-hidden mb-1">
        <div className={`h-full transition-all ${overshoot > 0 ? "bg-red-500" : "bg-emerald-500"}`}
             style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-foreground/50 mb-4">{pct}٪ من هدفك اليومي</p>

      {/* Macros consumed */}
      {(targetMacros?.protein || targetMacros?.carbs || targetMacros?.fat) && (
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <MacroBar label="بروتين" eaten={eaten.protein} target={targetMacros.protein} color="bg-rose-500" />
          <MacroBar label="كارب"   eaten={eaten.carbs}   target={targetMacros.carbs}   color="bg-amber-500" />
          <MacroBar label="دهون"   eaten={eaten.fat}     target={targetMacros.fat}     color="bg-sky-500" />
        </div>
      )}

      {/* Add row */}
      <div className="space-y-2 border-t border-foreground/10 pt-4">
        <Input placeholder="اسم الوجبة (مثلاً: دجاج مشوي)"
               value={name} onChange={(e) => setName(e.target.value)}
               data-testid="meal-name" />
        <div className="grid grid-cols-4 gap-2">
          <Input type="number" inputMode="numeric" placeholder="سعرة"
                 value={kcal} onChange={(e) => setKcal(e.target.value)}
                 data-testid="meal-kcal" />
          <Input type="number" inputMode="numeric" placeholder="بروتين"
                 value={protein} onChange={(e) => setProtein(e.target.value)}
                 data-testid="meal-protein" />
          <Input type="number" inputMode="numeric" placeholder="كارب"
                 value={carbs} onChange={(e) => setCarbs(e.target.value)}
                 data-testid="meal-carbs" />
          <Input type="number" inputMode="numeric" placeholder="دهون"
                 value={fat} onChange={(e) => setFat(e.target.value)}
                 data-testid="meal-fat" />
        </div>
        <Button
          onClick={addMeal}
          disabled={busy || !name.trim() || !kcal}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white gap-2"
          data-testid="meal-add">
          <Plus className="h-4 w-4" />
          إضافة وجبة
        </Button>
      </div>

      {/* Meal list */}
      {items.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-foreground/10 pt-4">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-2 rounded-xl bg-foreground/[0.03] px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{it.name}</p>
                <p className="text-[11px] text-foreground/60" dir="ltr">
                  {fmtNum(it.kcal)} سعرة · {fmtNum(it.protein_g)}پ / {fmtNum(it.carbs_g)}ك / {fmtNum(it.fat_g)}د
                </p>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive shrink-0"
                      onClick={() => removeMeal(it.id)} data-testid={`meal-del-${it.id}`}>
                <XIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function StatBox({ label, value, unit, accent, testid }) {
  return (
    <div className="rounded-xl border border-foreground/10 p-3 text-center" data-testid={testid}>
      <p className="text-[11px] text-foreground/60">{label}</p>
      <p className={`font-display font-bold text-xl tabular-nums mt-1 ${accent || ""}`} dir="ltr">{value}</p>
      <p className="text-[10px] text-foreground/50">{unit}</p>
    </div>
  );
}

function MacroBar({ label, eaten, target, color }) {
  const pct = target > 0 ? Math.min(100, Math.round((eaten / target) * 100)) : 0;
  return (
    <div>
      <p className="text-[11px] text-foreground/60 mb-1">
        {label} <span dir="ltr" className="tabular-nums">{fmtNum(eaten)}/{fmtNum(target)}g</span>
      </p>
      <div className="h-1.5 rounded-full bg-foreground/10 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ===========================================================================
// Weekly weigh-in — user logs weight, we show trend + push latest up so the
// days-remaining + BMI cards react to real progress.
// ===========================================================================
function WeightLogCard({ targetWeight, heightCm, initialWeight, speedKgPerWeek, startDate, onLatestChange, onWeightLogged }) {
  const [logs, setLogs] = useState([]);
  const [w, setW] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/users/me/weight-logs");
      const arr = Array.isArray(data) ? data : [];
      setLogs(arr);
      onLatestChange?.(arr[0] || null);
    } catch { /* silent */ }
  };
  useEffect(() => { reload(); }, []);

  const addWeight = async () => {
    if (!w) return;
    setBusy(true);
    try {
      const newKg = Number(w);
      await api.post("/users/me/weight-logs", { weight_kg: newKg });
      setW("");
      await reload();
      // Auto-recalculate calories + macros using the new weight.
      onWeightLogged?.(newKg);
    } catch { toast.error("تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  const removeLog = async (id) => {
    try { await api.delete(`/users/me/weight-logs/${id}`); await reload(); }
    catch { toast.error("تعذّر الحذف"); }
  };

  const latest = logs[0]?.weight_kg;
  const previous = logs[1]?.weight_kg;
  const delta = latest != null && previous != null ? +(latest - previous).toFixed(1) : null;
  const startWeight = logs.length ? logs[logs.length - 1].weight_kg : initialWeight;
  const totalChange = latest != null && startWeight != null ? +(latest - startWeight).toFixed(1) : null;
  const eff = latest ?? initialWeight;
  const goalDelta = eff != null && targetWeight ? +(eff - targetWeight).toFixed(1) : null;

  // ---- Days-to-goal (merged from the old separate card) ----
  const remainingKg = eff != null && targetWeight ? Math.abs(eff - targetWeight) : null;
  const totalDaysFromNow = speedKgPerWeek > 0 && remainingKg != null
    ? Math.ceil((remainingKg / speedKgPerWeek) * 7)
    : null;
  // Progress % of the total journey (start → target).
  const journeyPct = (startWeight != null && targetWeight && startWeight !== targetWeight && eff != null)
    ? Math.min(100, Math.max(0, Math.round(
        (Math.abs(startWeight - eff) / Math.abs(startWeight - targetWeight)) * 100
      )))
    : 0;

  return (
    <Card className="p-5 md:p-6 border-foreground/10" data-testid="weight-log-card">
      <div className="flex items-center gap-2 mb-4">
        <Scale className="h-5 w-5 text-orange-500" />
        <h3 className="font-display font-semibold text-lg">وزني ووصولي للهدف</h3>
      </div>

      {/* Big summary — 3 stat boxes */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatBox label="آخر وزن" value={latest != null ? fmtNum(latest) : "—"} unit="كجم" testid="wt-latest" />
        <StatBox label="التغيّر من آخر مرة"
                 value={delta == null ? "—" : (delta > 0 ? `+${fmtNum(delta)}` : fmtNum(delta))}
                 unit="كجم"
                 accent={delta == null ? "" : delta < 0 ? "text-emerald-600" : delta > 0 ? "text-red-600" : ""}
                 testid="wt-delta" />
        <StatBox label="باقي للهدف"
                 value={goalDelta == null ? "—" : fmtNum(Math.abs(goalDelta))}
                 unit="كجم"
                 testid="wt-remaining" />
      </div>

      {/* Goal progress bar + days-remaining — merged from the old هدف card */}
      {totalDaysFromNow != null && (
        <div className="mb-4 rounded-2xl bg-orange-50 border border-orange-100 p-4" data-testid="goal-progress">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-orange-600" />
              <p className="text-sm font-semibold text-orange-900">وصولك لهدفك</p>
            </div>
            <p className="font-display font-black text-2xl tabular-nums text-orange-700" dir="ltr">
              {fmtNum(totalDaysFromNow)}<span className="text-xs font-normal opacity-70 mr-1">يوم متبقي</span>
            </p>
          </div>
          <div className="h-2 rounded-full bg-white/70 overflow-hidden">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${journeyPct}%` }} />
          </div>
          <p className="text-[11px] text-orange-800/70 mt-2">
            قطعت {journeyPct}٪ من الرحلة · عند سرعة {speedKgPerWeek} كجم / أسبوع
          </p>
        </div>
      )}

      {/* Total change since start */}
      {totalChange != null && totalChange !== 0 && (
        <div className="mb-4 flex items-center gap-2 text-xs">
          {totalChange < 0 ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <ArrowDown className="h-3 w-3" /> نزلت <span dir="ltr">{fmtNum(Math.abs(totalChange))}</span> كجم من البداية
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
              <ArrowUp className="h-3 w-3" /> زدت <span dir="ltr">{fmtNum(totalChange)}</span> كجم من البداية
            </span>
          )}
        </div>
      )}

      {/* Add row */}
      <div className="flex gap-2 border-t border-foreground/10 pt-4">
        <Input type="number" inputMode="decimal" step="0.1" min="30" max="250"
               placeholder="وزنك اليوم بالكجم"
               value={w} onChange={(e) => setW(e.target.value)}
               data-testid="wt-input" />
        <Button
          onClick={addWeight}
          disabled={busy || !w}
          className="bg-orange-500 hover:bg-orange-600 text-white gap-2 shrink-0"
          data-testid="wt-add">
          <Plus className="h-4 w-4" />
          سجّل
        </Button>
      </div>
      <p className="text-[11px] text-foreground/50 mt-2">نصيحة: زِن نفسك في نفس الوقت من اليوم مرة في الأسبوع. سعراتك وماكروزك تتحدث تلقائياً بعد كل تسجيل.</p>

      {/* History */}
      {logs.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-foreground/10 pt-4">
          {logs.slice(0, 8).map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-2 rounded-xl bg-foreground/[0.03] px-3 py-2">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs text-foreground/60 tabular-nums" dir="ltr">{l.date}</span>
                <span className="font-semibold text-sm tabular-nums" dir="ltr">{fmtNum(l.weight_kg)} كجم</span>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive shrink-0"
                      onClick={() => removeLog(l.id)} data-testid={`wt-del-${l.id}`}>
                <XIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {logs.length > 8 && (
            <p className="text-[11px] text-foreground/50 text-center">وأقدم {fmtNum(logs.length - 8)} قراءة...</p>
          )}
        </div>
      )}
    </Card>
  );
}
